/**
 * Admin Audit-Log Viewer (PR #5f).
 *
 * Reads from GET /admin/audit via TanStack Query. Supports filtering by
 * category, action (partial text match), and date range, plus server-side
 * pagination.
 *
 * The audit log is read-only; this component has no mutations.
 *
 * Access: requires `admin.audit.view`. The tab is only mounted when the
 * user has that permission; the parent `AdminModule` gates it.
 *
 * Payload rendering: `payload` is free-form JSON. We render it as a
 * collapsible pre-formatted string rather than navigating its keys, so
 * unexpected shapes cannot crash the component.
 */

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { ClipboardList } from 'lucide-react';
import type { AuditEventCategory, AuditListQuery } from '@cortex/domain';
import { AUDIT_CATEGORIES } from '@cortex/domain';

import { cortexApi } from '@/services/cortexApi';
import { resolveApiError } from '@/services/errorMessages';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { StatusBadge, type StatusBadgeTone } from '@/components/ui/StatusBadge';
import { PageHeader } from '@/components/ui/PageHeader';
import {
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
} from '@/components/ui/DataTable';

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatTs = (iso: string) => {
  try {
    return format(parseISO(iso), 'dd MMM yyyy HH:mm:ss');
  } catch {
    return iso;
  }
};

const CATEGORY_TONES: Readonly<Partial<Record<AuditEventCategory, StatusBadgeTone>>> = {
  Authentication: 'info',
  Authorization: 'warning',
  Quote: 'active',
  Bind: 'active',
  Facility: 'neutral',
  Entity: 'neutral',
  User: 'neutral',
  Role: 'neutral',
  Admin: 'warning',
  Impersonation: 'warning',
  System: 'neutral',
  Engine: 'neutral',
};

const toneForCategory = (category: AuditEventCategory): StatusBadgeTone =>
  CATEGORY_TONES[category] ?? 'neutral';

const FILTER_SELECT =
  'h-8 rounded-md border border-input bg-background px-2 text-xs shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none';

// ─── component ────────────────────────────────────────────────────────────────

/**
 * Controls the query filters the user can apply.
 * `undefined` values are omitted from the API request.
 */
interface Filters {
  category: AuditEventCategory | undefined;
  action: string;
  fromDate: string;
  toDate: string;
}

const defaultFilters = (): Filters => ({
  category: undefined,
  action: '',
  fromDate: '',
  toDate: '',
});

export function AuditTab() {
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const query = useMemo<Partial<AuditListQuery>>(() => {
    const q: Partial<AuditListQuery> = { page, pageSize: PAGE_SIZE };
    if (filters.category !== undefined) q.category = filters.category;
    if (filters.action.trim().length > 0) q.action = filters.action.trim();
    if (filters.fromDate.length > 0) q.fromDate = new Date(filters.fromDate).toISOString();
    if (filters.toDate.length > 0) q.toDate = new Date(filters.toDate).toISOString();
    return q;
  }, [filters, page]);

  const auditQuery = useQuery({
    queryKey: ['admin', 'audit', query],
    queryFn: () => cortexApi.audit.list(query),
  });

  const events = auditQuery.data?.data ?? [];
  const total = auditQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isLoading = auditQuery.isLoading;
  const errorMessage = auditQuery.error != null ? resolveApiError(auditQuery.error).message : null;

  const handleFilterChange = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(defaultFilters());
    setPage(1);
  };

  const hasFilters =
    filters.category !== undefined ||
    filters.action.trim().length > 0 ||
    filters.fromDate.length > 0 ||
    filters.toDate.length > 0;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Audit Log"
        subtitle="Append-only record of all system events. 7-year POPIA retention."
        toolbar={
          hasFilters ? (
            <Button type="button" variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear filters
            </Button>
          ) : undefined
        }
      />

      {/* Filters */}
      <div className="mb-4 flex flex-wrap gap-2 rounded-lg border border-border bg-muted/40 p-3">
        <select
          value={filters.category ?? ''}
          onChange={(e) =>
            handleFilterChange({
              category: e.target.value === '' ? undefined : (e.target.value as AuditEventCategory),
            })
          }
          className={FILTER_SELECT}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {AUDIT_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>

        <Input
          type="search"
          value={filters.action}
          onChange={(e) => handleFilterChange({ action: e.target.value })}
          placeholder="Filter by action…"
          className="h-8 w-48 text-xs"
          aria-label="Filter by action"
        />

        <div className="flex items-center gap-1.5">
          <label htmlFor="audit-from" className="text-xs text-muted-foreground">
            From
          </label>
          <input
            id="audit-from"
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange({ fromDate: e.target.value })}
            className={FILTER_SELECT}
          />
        </div>

        <div className="flex items-center gap-1.5">
          <label htmlFor="audit-to" className="text-xs text-muted-foreground">
            To
          </label>
          <input
            id="audit-to"
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange({ toDate: e.target.value })}
            className={FILTER_SELECT}
          />
        </div>
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-card shadow-sm">
        {isLoading && <TableSkeleton />}

        {!isLoading && errorMessage && (
          <div
            className="border-b border-destructive/20 bg-destructive/10 px-4 py-6 text-center text-xs text-destructive"
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        {!isLoading && !errorMessage && events.length === 0 && (
          <EmptyState
            icon={ClipboardList}
            title={hasFilters ? 'No events match these filters.' : 'No audit events recorded yet.'}
            description={
              hasFilters
                ? 'Try clearing the filters to see all events.'
                : 'Audit events will appear here as the system is used.'
            }
          />
        )}

        {!isLoading && !errorMessage && events.length > 0 && (
          <div className="overflow-x-auto">
            <DataTable data-testid="audit-table">
              <DataTableHead>
                <DataTableHeader>Timestamp</DataTableHeader>
                <DataTableHeader>Category</DataTableHeader>
                <DataTableHeader>Action</DataTableHeader>
                <DataTableHeader>Actor</DataTableHeader>
                <DataTableHeader>Entity</DataTableHeader>
                <DataTableHeader>Payload</DataTableHeader>
              </DataTableHead>
              <DataTableBody>
                {events.map((event) => {
                  const isExpanded = expandedId === event.id;
                  return (
                    <DataTableRow key={event.id}>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground whitespace-nowrap">
                        {formatTs(event.occurredAt)}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge
                          tone={toneForCategory(event.category)}
                          label={event.category}
                        />
                      </td>
                      <td className="px-4 py-2 font-mono text-micro">{event.action}</td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {event.actorDisplayName ?? (
                          <span className="font-mono text-xs">{event.actorUserId ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground">
                        {event.entityName ?? (
                          <span className="font-mono text-xs">{event.entityId ?? '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => setExpandedId(isExpanded ? null : event.id)}
                          className="text-xs text-primary underline-offset-2 hover:underline focus:outline-none"
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? 'Hide' : 'Show'}
                        </button>
                        {isExpanded && (
                          <pre className="mt-1 max-h-48 max-w-xs overflow-auto rounded bg-muted p-2 text-xs">
                            {JSON.stringify(event.payload, null, 2)}
                          </pre>
                        )}
                      </td>
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {total.toLocaleString()} event{total !== 1 ? 's' : ''} · page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="space-y-2 p-4" aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}
